import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Filter } from 'lucide-react';
import { engagements } from './engagementsData';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

const Engagements = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCapability, setSelectedCapability] = useState('');
  const [selectedTechnology, setSelectedTechnology] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [expandedTags, setExpandedTags] = useState({});

  const [sectionRef, sectionVisible] = useScrollAnimation(0.1);

  // Filter engagements based on selected filters and search
  const filteredEngagements = useMemo(() => {
    return engagements.filter(engagement => {
      // Industry filter
      if (selectedIndustry && engagement.industry !== selectedIndustry) {
        return false;
      }

      // Capability filter
      if (selectedCapability && !engagement.capabilities.includes(selectedCapability)) {
        return false;
      }

      // Technology filter
      if (selectedTechnology && !engagement.technologies.includes(selectedTechnology)) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchableText = [
          engagement.title,
          engagement.client,
          engagement.context,
          engagement.approach,
          engagement.industry,
          ...engagement.capabilities,
          ...engagement.technologies,
          ...engagement.outcomes
        ].join(' ').toLowerCase();

        if (!searchableText.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [searchQuery, selectedIndustry, selectedCapability, selectedTechnology]);

  // Determine if we should show pagination (only when not searching)
  const isSearching = searchQuery || selectedIndustry || selectedCapability || selectedTechnology;
  const displayedEngagements = isSearching
    ? filteredEngagements
    : filteredEngagements.slice(0, visibleCount);
  const hasMore = !isSearching && visibleCount < filteredEngagements.length;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedIndustry('');
    setSelectedCapability('');
    setSelectedTechnology('');
    setVisibleCount(5);
  };

  const loadMore = () => {
    const firstNewIndex = visibleCount; // Index of first new engagement
    setVisibleCount(prev => prev + 5);

    // Scroll to first new engagement after DOM updates
    setTimeout(() => {
      const cards = document.querySelectorAll('[data-engagement-card]');
      if (cards[firstNewIndex]) {
        cards[firstNewIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const hasActiveFilters = searchQuery || selectedIndustry || selectedCapability || selectedTechnology;

  const toggleExpand = (id, cardElement) => {
    const isExpanding = expandedId !== id;
    setExpandedId(expandedId === id ? null : id);

    // Track engagement card expansion
    if (isExpanding && window.gtag) {
      const engagement = engagements.find(e => e.id === id);
      window.gtag('event', 'engagement_card_expand', {
        engagement_title: engagement?.title || 'unknown',
        engagement_id: id,
        industry: engagement?.industry || 'unknown'
      });
    }

    // Scroll to top of card when expanding - wait for transition to complete, then use native scrollIntoView
    if (isExpanding && cardElement) {
      setTimeout(() => {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  };

  // Get unique values that exist in the data
  const availableIndustries = [...new Set(engagements.map(e => e.industry))].sort();
  const availableCapabilities = [...new Set(engagements.flatMap(e => e.capabilities))].sort();
  const availableTechnologies = [...new Set(engagements.flatMap(e => e.technologies))].filter(t => t).sort();

  return (
    <div
      ref={sectionRef}
      className="py-20 relative overflow-hidden"
      id="engagements"
      style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #e8f4f4 100%)' }}
    >
      {/* Decorative gradient blobs like hero */}
      <svg
        className="absolute -left-20 top-20 w-72 h-72 opacity-20"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{ filter: 'blur(40px)' }}
      >
        <defs>
          <linearGradient id="engBlob1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#2d8a8a', stopOpacity: 0.5 }} />
            <stop offset="100%" style={{ stopColor: '#1a2332', stopOpacity: 0.2 }} />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="80" fill="url(#engBlob1)" />
      </svg>
      <svg
        className="absolute -right-16 bottom-32 w-64 h-64 opacity-15"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{ filter: 'blur(50px)' }}
      >
        <defs>
          <linearGradient id="engBlob2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#2d8a8a', stopOpacity: 0.4 }} />
            <stop offset="100%" style={{ stopColor: '#4a9e9e', stopOpacity: 0.2 }} />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="80" fill="url(#engBlob2)" />
      </svg>

      {/* Discreet playbook X element */}
      <svg className="absolute top-32 right-8 w-16 h-16 opacity-[0.04]" viewBox="0 0 40 40">
        <line x1="5" y1="5" x2="35" y2="35" stroke="#2d8a8a" strokeWidth="3" strokeLinecap="round" />
        <line x1="35" y1="5" x2="5" y2="35" stroke="#2d8a8a" strokeWidth="3" strokeLinecap="round" />
      </svg>

      {/* Discreet playbook O element */}
      <svg className="absolute bottom-40 left-12 w-20 h-20 opacity-[0.03]" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="15" fill="none" stroke="#1a2332" strokeWidth="3" />
      </svg>

      <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 delay-200 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Engagements</h2>
          <p style={{ color: '#666' }}>
            {isSearching
              ? `${filteredEngagements.length} of ${engagements.length} engagements (filtered)`
              : `Showing ${displayedEngagements.length} of ${engagements.length} engagements`
            }
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#999' }} />
            <input
              type="text"
              placeholder="Search engagements..."
              value={searchQuery}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchQuery(newValue);

                // Track search after user stops typing (debounced)
                if (newValue && window.gtag) {
                  clearTimeout(window.searchDebounceTimer);
                  window.searchDebounceTimer = setTimeout(() => {
                    window.gtag('event', 'engagement_search', {
                      search_query: newValue
                    });
                  }, 1000);
                }
              }}
              className="w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 shadow-sm hover:shadow-md"
              style={{
                borderColor: '#e5e7eb',
                backgroundColor: '#f8f9fa'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2d8a8a';
                e.target.style.boxShadow = '0 0 0 3px rgba(45, 138, 138, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = '';
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-all duration-300 hover:bg-gray-200"
                style={{ color: '#666' }}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 transition-colors duration-300"
            style={{ color: '#666' }}
          >
            <Filter className="w-5 h-5" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {/* Filters */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${showFilters ? 'block' : 'hidden md:grid'}`}>
            {/* Industry Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#666' }}>Industry</label>
              <select
                value={selectedIndustry}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSelectedIndustry(newValue);
                  if (newValue && window.gtag) {
                    window.gtag('event', 'filter_apply', {
                      filter_type: 'industry',
                      filter_value: newValue
                    });
                  }
                }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 bg-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                style={{ borderColor: '#e5e7eb' }}
              >
                <option value="">All Industries</option>
                {availableIndustries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            {/* Capability Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#666' }}>Capability</label>
              <select
                value={selectedCapability}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSelectedCapability(newValue);
                  if (newValue && window.gtag) {
                    window.gtag('event', 'filter_apply', {
                      filter_type: 'capability',
                      filter_value: newValue
                    });
                  }
                }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 bg-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                style={{ borderColor: '#e5e7eb' }}
              >
                <option value="">All Capabilities</option>
                {availableCapabilities.map(capability => (
                  <option key={capability} value={capability}>{capability}</option>
                ))}
              </select>
            </div>

            {/* Technology Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#666' }}>Technology</label>
              <select
                value={selectedTechnology}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSelectedTechnology(newValue);
                  if (newValue && window.gtag) {
                    window.gtag('event', 'filter_apply', {
                      filter_type: 'technology',
                      filter_value: newValue
                    });
                  }
                }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 bg-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                style={{ borderColor: '#e5e7eb' }}
              >
                <option value="">All Technologies</option>
                {availableTechnologies.map(tech => (
                  <option key={tech} value={tech}>{tech}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm flex items-center gap-1 transition-all duration-300 hover:scale-105"
              style={{ color: '#2d8a8a' }}
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          )}
        </div>

        {/* Engagements List */}
        <div className="space-y-4">
          {filteredEngagements.length === 0 ? (
            <div className="text-center py-12 rounded-xl shadow-inner" style={{ backgroundColor: '#f8f9fa' }}>
              <p style={{ color: '#666' }}>No engagements match your filters.</p>
              <button
                onClick={clearFilters}
                className="mt-4 transition-colors duration-300"
                style={{ color: '#2d8a8a' }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            displayedEngagements.map((engagement, index) => (
              <div
                key={engagement.id}
                data-engagement-card
                className="rounded-xl overflow-hidden transition-all duration-500 border bg-white shadow-md hover:shadow-xl transform hover:-translate-y-1"
                style={{
                  borderColor: expandedId === engagement.id ? '#2d8a8a' : '#e5e7eb',
                  animationDelay: `${index * 100}ms`,
                  opacity: sectionVisible ? 1 : 0,
                  transform: sectionVisible ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${index * 100}ms`,
                  scrollMarginTop: '120px'
                }}
              >
                {/* Card Header */}
                <div
                  className="p-6 cursor-pointer"
                  onClick={(e) => toggleExpand(engagement.id, e.currentTarget.parentElement)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold" style={{ color: '#1a2332' }}>
                          {engagement.title}
                        </h3>
                        <span
                          className="text-xs font-medium px-3 py-1 rounded-full"
                          style={{ backgroundColor: 'rgba(45, 138, 138, 0.1)', color: '#2d8a8a' }}
                        >
                          {engagement.keyMetric}
                        </span>
                      </div>
                      <p className="text-sm mb-3" style={{ color: '#666' }}>
                        {engagement.client}
                      </p>
                      <div className="flex flex-wrap gap-2 items-center">
                        <span
                          className="text-xs px-3 py-1 rounded-full font-medium"
                          style={{ backgroundColor: '#2d8a8a', color: 'white' }}
                        >
                          {engagement.industry}
                        </span>
                        {(expandedTags[engagement.id] ? engagement.capabilities : engagement.capabilities.slice(0, 2)).map((cap, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-3 py-1 rounded-full"
                            style={{ backgroundColor: '#e8f4f4', color: '#2d8a8a' }}
                          >
                            {cap}
                          </span>
                        ))}
                        {!expandedTags[engagement.id] && engagement.capabilities.length > 2 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTags(prev => ({ ...prev, [engagement.id]: true }));
                            }}
                            className="text-xs px-2 py-1 rounded-full transition-all duration-200 hover:bg-gray-100"
                            style={{ color: '#2d8a8a' }}
                          >
                            +{engagement.capabilities.length - 2} more
                          </button>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 ml-4 transition-transform duration-300 ${
                        expandedId === engagement.id ? 'rotate-180' : ''
                      }`}
                      style={{ color: '#2d8a8a' }}
                    />
                  </div>
                </div>

                {/* Expanded Content */}
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    expandedId === engagement.id ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 space-y-6 border-t pt-6" style={{ borderColor: '#e5e7eb' }}>
                    {/* Context */}
                    <div>
                      <h4 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#2d8a8a' }}>
                        Context
                      </h4>
                      <p style={{ color: '#333' }}>{engagement.context}</p>
                    </div>

                    {/* Approach */}
                    <div>
                      <h4 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#2d8a8a' }}>
                        Approach
                      </h4>
                      <p style={{ color: '#333' }}>{engagement.approach}</p>
                    </div>

                    {/* Architecture */}
                    {engagement.architecture && (
                      <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#2d8a8a' }}>
                          Architecture
                        </h4>
                        <p style={{ color: '#333' }}>{engagement.architecture}</p>
                      </div>
                    )}

                    {/* Outcomes */}
                    <div>
                      <h4 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: '#2d8a8a' }}>
                        Outcomes
                      </h4>
                      <ul className="space-y-2 pl-0">
                        {engagement.outcomes.map((outcome, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#2d8a8a' }} />
                            <span style={{ color: '#333' }}>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    {engagement.technologies.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#2d8a8a' }}>
                          Technologies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {engagement.technologies.map((tech, idx) => (
                            <span
                              key={idx}
                              className="text-sm px-3 py-1 rounded-lg border transition-all duration-300 hover:shadow-sm"
                              style={{ borderColor: '#e5e7eb', color: '#333', backgroundColor: '#f8f9fa' }}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* See More Button */}
        {hasMore && (
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                loadMore();
                if (window.gtag) {
                  window.gtag('event', 'load_more_engagements', {
                    current_count: visibleCount,
                    remaining_count: filteredEngagements.length - visibleCount
                  });
                }
              }}
              className="inline-flex items-center px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#2d8a8a', color: 'white' }}
            >
              See More
              <ChevronDown className="ml-2 h-5 w-5" />
            </button>
            <p className="mt-3 text-sm" style={{ color: '#999' }}>
              {filteredEngagements.length - visibleCount} more engagements
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Engagements;
